import {
  MASK_HEIGHT,
  MASK_WIDTH,
  STAGE_THRESHOLD,
  TOOL_NAMES,
  TOOL_RADII,
  isToolIndex,
  type ToolIndex,
} from '../config/constants';
import type { CleaningResult, GameState, MaskPoint, ModelSnapshot } from '../game/types';
import { interpolateTrail } from '../input/PointerTrail';
import { MaskLayer } from './MaskLayer';

function isTargetPixel(stage: ToolIndex, x: number, y: number): boolean {
  switch (stage) {
    case 0:
      return true;
    case 1:
      return x % 24 <= 3 || y % 24 <= 3;
    case 2:
      return ((x * 17 + y * 31) & 3) !== 0;
    case 3:
      return ((x + y) & 1) === 0;
  }
}

export class CleaningModel {
  readonly layers = TOOL_NAMES.map((_, index) => new MaskLayer(
    MASK_WIDTH,
    MASK_HEIGHT,
    (x, y) => isTargetPixel(index as ToolIndex, x, y),
  ));

  state: GameState = 'not-started';
  expectedStage = 0;
  selectedTool: ToolIndex = 0;
  private elapsedBeforeRun = 0;
  private startedAt = 0;

  get totalProgress(): number {
    if (this.state === 'completed') {
      return 1;
    }
    if (this.expectedStage >= TOOL_NAMES.length) {
      return 1;
    }
    const stage = this.layers[this.expectedStage];
    if (!stage) {
      return 0;
    }
    const normalized = Math.min(stage.clearedRatio / STAGE_THRESHOLD, 1);
    return (this.expectedStage + normalized) / TOOL_NAMES.length;
  }

  get elapsedMs(): number {
    return this.elapsedBeforeRun + (this.state === 'running' ? performance.now() - this.startedAt : 0);
  }

  start(): void {
    this.resetLayers();
    this.state = 'running';
    this.startedAt = performance.now();
  }

  restart(): void {
    this.start();
  }

  returnToStart(): void {
    this.resetLayers();
    this.state = 'not-started';
  }

  pause(): boolean {
    if (this.state !== 'running') {
      return false;
    }
    this.elapsedBeforeRun += performance.now() - this.startedAt;
    this.state = 'paused';
    return true;
  }

  resume(): boolean {
    if (this.state !== 'paused') {
      return false;
    }
    this.state = 'running';
    this.startedAt = performance.now();
    return true;
  }

  selectTool(tool: number): boolean {
    if (!isToolIndex(tool)) {
      return false;
    }
    this.selectedTool = tool;
    return tool === this.expectedStage;
  }

  canClean(): 'ok' | 'paused' | 'wrong-tool' {
    if (this.state === 'paused') {
      return 'paused';
    }
    if (this.state !== 'running' || this.selectedTool !== this.expectedStage) {
      return 'wrong-tool';
    }
    return 'ok';
  }

  applySegment(from: MaskPoint, to: MaskPoint): CleaningResult | null {
    if (this.canClean() !== 'ok' || !isToolIndex(this.expectedStage)) {
      return null;
    }

    const stage = this.expectedStage;
    const radius = TOOL_RADII[stage];
    const points = interpolateTrail(from, to, Math.max(1, radius * 0.35));
    const layer = this.layers[stage];
    if (!layer) {
      return null;
    }

    let changed = 0;
    for (const point of points) {
      changed += layer.clearCircle(point.x, point.y, radius);
    }

    const stageProgress = layer.clearedRatio;
    let stageCompleted = false;
    let gameCompleted = false;
    if (changed > 0 && stageProgress >= STAGE_THRESHOLD) {
      stageCompleted = true;
      this.expectedStage += 1;
      if (this.expectedStage >= TOOL_NAMES.length) {
        this.elapsedBeforeRun += performance.now() - this.startedAt;
        this.state = 'completed';
        gameCompleted = true;
      } else {
        this.selectedTool = this.expectedStage as ToolIndex;
      }
    }

    return {
      changed,
      stage,
      points,
      stageProgress,
      totalProgress: this.totalProgress,
      stageCompleted,
      gameCompleted,
    };
  }

  snapshot(): ModelSnapshot {
    return {
      version: 1,
      state: this.state,
      expectedStage: this.expectedStage,
      selectedTool: this.selectedTool,
      elapsedMs: Math.round(this.elapsedMs),
      layers: this.layers.map((layer) => layer.serialize()),
    };
  }

  restore(snapshot: ModelSnapshot): boolean {
    if (
      snapshot.version !== 1
      || !isToolIndex(snapshot.selectedTool)
      || snapshot.expectedStage < 0
      || snapshot.expectedStage > TOOL_NAMES.length
      || snapshot.layers.length !== TOOL_NAMES.length
    ) {
      return false;
    }

    try {
      this.layers.forEach((layer, index) => {
        const encoded = snapshot.layers[index];
        if (typeof encoded !== 'string') {
          throw new Error('遮罩存档缺失');
        }
        layer.restore(encoded);
      });
    } catch {
      return false;
    }

    this.state = snapshot.state;
    this.expectedStage = snapshot.expectedStage;
    this.selectedTool = snapshot.selectedTool;
    this.elapsedBeforeRun = Math.max(0, snapshot.elapsedMs);
    this.startedAt = performance.now();
    return true;
  }

  private resetLayers(): void {
    this.layers.forEach((layer) => layer.reset());
    this.expectedStage = 0;
    this.selectedTool = 0;
    this.elapsedBeforeRun = 0;
    this.startedAt = 0;
  }
}
